/**
 * Modal que informa al usuario sobre nuevas versiones disponibles con changelog.
 */
import { Modal, Alert, Button, Space, Typography, Tag, Progress } from "antd";
import { ExclamationCircleOutlined, DesktopOutlined, ApiOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { clearBrowserCache } from "utils/storage/clearBrowserCache";

const { Text, Title } = Typography;

const AppVersionModal = ({ visible, onConfirm, backVersion, frontVersion }) => {
  const primaryColor = "#791ea3";
  const [isMobile, setIsMobile] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 480);
    check();

    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const handleActualizar = () => {
    if (updating) return;

    setUpdating(true);
    setProgress(0);

    const interval = setInterval(() => {
      setProgress((prev) => {
        const next = prev + 20;

        if (next >= 100) {
          clearInterval(interval);

          setTimeout(() => {
            clearBrowserCache();
            onConfirm?.(); // esto debería hacer reload
          }, 250);

          return 100;
        }

        return next;
      });
    }, 200);
  };

  return (
    <Modal
      open={visible}
      closable={false}
      maskClosable={false}
      footer={null}
      centered
      width={isMobile ? "92%" : 520}
      styles={{
        body: {
          padding: isMobile ? "24px 18px" : "34px",
        },
      }}
    >
      <div style={{ textAlign: "center" }}>
        <div
          style={{
            width: isMobile ? 64 : 74,
            height: isMobile ? 64 : 74,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #f5ecfa, #ffffff)",
            border: "1px solid #ead8f3",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 18px",
          }}
        >
          <ExclamationCircleOutlined
            style={{
              fontSize: isMobile ? 34 : 40,
              color: primaryColor,
            }}
          />
        </div>

        <Title
          level={3}
          style={{
            margin: 0,
            marginBottom: 8,
            fontSize: isMobile ? 20 : 24,
            color: "#262626",
          }}
        >
          Actualización disponible
        </Title>

        <Text type="secondary" style={{ fontSize: isMobile ? 13 : 14 }}>
          Se detectó una nueva versión de OneFIN.
        </Text>

        <Alert
          message="Es necesario actualizar la aplicación"
          description="Para continuar operando correctamente, se cerrará la sesión actual y se recargará la página con la última versión disponible."
          type="warning"
          showIcon
          style={{
            marginTop: 22,
            marginBottom: 18,
            textAlign: "left",
            borderRadius: 8,
          }}
        />

        <div
          style={{
            background: "#fafafa",
            border: "1px solid #f0f0f0",
            borderRadius: 10,
            padding: isMobile ? 12 : 14,
            marginBottom: 24,
            textAlign: "left",
          }}
        >
          <Space direction="vertical" size={10} style={{ width: "100%" }}>
            <VersionRow
              icon={<DesktopOutlined />}
              label="Cliente"
              value={frontVersion || "No informado"}
              highlight
              color={primaryColor}
            />

            <VersionRow
              icon={<ApiOutlined />}
              label="API"
              highlight
              color={primaryColor}
              value={backVersion || "No informado"}
            />
          </Space>
        </div>

        {updating && (
          <div style={{ marginBottom: 18, textAlign: "left" }}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Aplicando actualización...
            </Text>

            <Progress
              percent={progress}
              status={progress < 100 ? "active" : "success"}
              strokeColor={primaryColor}
              style={{ marginTop: 8 }}
            />
          </div>
        )}

        <Button
          type="primary"
          size="large"
          block={isMobile}
          onClick={handleActualizar}
          loading={updating}
          disabled={updating}
          style={{
            backgroundColor: primaryColor,
            borderColor: primaryColor,
            borderRadius: 8,
            minWidth: isMobile ? "100%" : 180,
            height: 42,
            fontWeight: 500,
          }}
        >
          {updating ? "Actualizando..." : "Actualizar ahora"}
        </Button>
      </div>
    </Modal>
  );
};

const VersionRow = ({ icon, label, value, highlight = false, color }) => {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
      }}
    >
      <Space size={8}>
        <span style={{ color: highlight ? color : "#8c8c8c" }}>{icon}</span>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {label}
        </Text>
      </Space>

      <Tag
        color="default"
        style={{
          margin: 0,
          borderColor: highlight ? color : "#d9d9d9",
          color: highlight ? color : "#595959",
          fontWeight: highlight ? 600 : 400,
        }}
      >
        {value}
      </Tag>
    </div>
  );
};

export default AppVersionModal;
